import React, { useState, useRef, useEffect } from 'react'
import { CheckCircle, XCircle, Lightbulb, ArrowRight, Sparkles } from 'lucide-react'
import KaTeXRenderer from '../../../common/KaTeXRenderer'
import { LessonStatsService } from '../../../../services/stats/LessonStatsService'
import { aiService } from '../../../../services/ai/AIService'
import { soundService } from '../../../../services/audio/SoundEffectService'
import { useAuth } from '../../../../contexts/AuthContext'
import { celebrateCorrect, WRONG_ENCOURAGE } from '../effects/feedbackEffects'

interface InteractiveQuestionProps {
  question: string
  options: string[]
  correctAnswer: number
  explanation: string
  hint?: string
  image?: string // ✅ Support for question images
  imageAlt?: string // ✅ Alt text for accessibility
  onAnswer?: (isCorrect: boolean) => void
  difficulty?: 'easy' | 'medium' | 'hard'
  isCompleted?: boolean // ✅ Prop để track completion status
  lessonId?: string // ✅ Prop để track lesson ID
}

const InteractiveQuestion: React.FC<InteractiveQuestionProps> = ({
  question,
  options,
  correctAnswer,
  explanation,
  hint,
  image, // ✅ Question image
  imageAlt, // ✅ Alt text for image
  onAnswer,
  difficulty = 'medium',
  isCompleted = false, // ✅ Default value
  lessonId // ✅ Lesson ID for stats tracking
}) => {
  // ✨ Shuffle options khi component mount để tránh ghi nhớ vị trí
  const [shuffledData] = useState(() => {
    const indices = options.map((_, i) => i)
    // Fisher-Yates shuffle
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]]
    }
    
    const shuffledOptions = indices.map(i => options[i])
    const newCorrectAnswer = indices.indexOf(correctAnswer)
    
    return {
      options: shuffledOptions,
      correctAnswer: newCorrectAnswer,
      originalToShuffled: indices
    }
  })
  
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  const [showResult, setShowResult] = useState(false)
  const [showHint, setShowHint] = useState(false)
  const [showExplanation, setShowExplanation] = useState(false)
  const [hasRecordedStats, setHasRecordedStats] = useState(false) // ✅ Chỉ đếm stats lần đầu
  const isProcessingRef = useRef(false) // ✅ Ngăn multiple clicks
  const containerRef = useRef<HTMLDivElement | null>(null) // 🎉 Vị trí bắn pháo giấy
  const [aiExplanation, setAIExplanation] = useState<string>('')
  const [isLoadingAI, setIsLoadingAI] = useState(false)
  const [showAIExplanation, setShowAIExplanation] = useState(false)
  
  const { user } = useAuth()

  // ✅ FIX: Initialize state based on completion status
  useEffect(() => {
    if (isCompleted) {
      // Nếu đã completed, set state để hiện kết quả đúng
      setSelectedAnswer(shuffledData.correctAnswer)
      setShowResult(true)
      setShowExplanation(true)
      setHasRecordedStats(true) // Đã có stats rồi
      console.log(`🔄 Question ${isCompleted ? 'completed' : 'pending'} - restoring state`)
    }
  }, [isCompleted, shuffledData.correctAnswer])

  const handleAnswerSelect = (index: number) => {
    console.log(`\n🎯 ========== ANSWER SELECT CALLED ==========`)
    console.log(`   Index: ${index}, showResult: ${showResult}, isProcessing: ${isProcessingRef.current}`)
    
    // ✅ TRIPLE PROTECTION: Ngăn multiple clicks (nhưng cho phép chọn lại khi sai)
    // 1. Check state - block nếu đã completed hoặc đã chọn đúng
    if (isCompleted || (showResult && selectedAnswer === correctAnswer)) {
      console.log(`🚫 BLOCKED: Already completed or answered correctly`)
      return
    }
    
    // 2. Check ref (immediate flag)
    if (isProcessingRef.current) {
      console.log(`🚫 BLOCKED: Already processing (ref=true)`)
      return
    }
    
    // 3. Set flag immediately BEFORE any async operations
    isProcessingRef.current = true
    console.log(`✅ PROCESSING: Flag set to true`)
    
    // ✅ NOTE: onFirstInteraction will be called from InteractiveContentRenderer's onAnswer callback
    
    // Update state - reset previous result if retrying
    setSelectedAnswer(index)
    setShowResult(true)
    setShowExplanation(false) // Reset explanation when retrying
    const isCorrect = index === shuffledData.correctAnswer
    
    console.log(`🎯 Answer: selected=${index}, correct=${shuffledData.correctAnswer}, isCorrect=${isCorrect}`)
    
    // 📊 Track statistics ONLY on first attempt
    if (!hasRecordedStats) {
      if (isCorrect) {
        console.log(`✅ Recording correct answer (FIRST ATTEMPT) for lessonId: ${lessonId}...`)
        LessonStatsService.recordCorrectAnswer(lessonId)
      } else {
        console.log(`❌ Recording wrong answer (FIRST ATTEMPT) for lessonId: ${lessonId}...`)
        LessonStatsService.recordWrongAnswer(lessonId)
      }
      setHasRecordedStats(true) // Mark as recorded
    } else {
      console.log(`📊 Stats already recorded, skipping...`)
    }
    
    // ✅ FIX: Logic mới - chỉ mở khóa nếu đúng
    if (isCorrect) {
      // 🔊 Phát âm thanh đúng + 🎉 pháo giấy chúc mừng
      soundService.playCorrect()
      celebrateCorrect(containerRef.current)

      // Đúng: hiện lời giải + mở khóa nội dung tiếp theo
      setTimeout(() => {
        setShowExplanation(true)
        console.log(`📖 Explanation shown for CORRECT answer`)
        
        // Mở khóa nội dung tiếp theo
        if (onAnswer) {
          console.log(`📤 Calling onAnswer callback for CORRECT answer...`)
          onAnswer(true)
        }
      }, 1000)
    } else {
      // 🔊 Phát âm thanh sai
      soundService.playIncorrect()
      
      // Sai: hiện lời giải + KHÔNG mở khóa + yêu cầu chọn lại
      setTimeout(() => {
        setShowExplanation(true)
        console.log(`📖 Explanation shown for WRONG answer`)
        // KHÔNG gọi onAnswer() để không mở khóa nội dung tiếp theo
      }, 1000)
    }
    
    console.log(`🎯 ========== END ANSWER SELECT ==========\n`)
  }

  const getDifficultyColor = () => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'hard': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getDifficultyText = () => {
    switch (difficulty) {
      case 'easy': return 'Dễ'
      case 'medium': return 'Trung bình'
      case 'hard': return 'Khó'
      default: return 'Trung bình'
    }
  }

  const reset = () => {
    setSelectedAnswer(null)
    setShowResult(false)
    setShowHint(false)
    setShowExplanation(false)
    // ✅ DON'T reset hasRecordedStats - keep stats from first attempt
    isProcessingRef.current = false // ✅ Reset flag để cho phép answer lại
    console.log('🔄 Question reset, ready for new answer (stats preserved)')
    // Don't call onAnswer on reset - only when user actually answers
  }

  const handleAskAI = async () => {
    if (isLoadingAI) return
    if (!user) {
      setShowAIExplanation(true)
      setAIExplanation('⚠️ Bạn cần đăng nhập để sử dụng Askbot AI.')
      return
    }

    // 🔊 Phát âm thanh bí ẩn khi hỏi AI
    soundService.playAIThinking()

    // 📊 Ghi nhận thống kê sử dụng AI
    console.log(`🤖 Recording AI usage for lessonId: ${lessonId}`)
    LessonStatsService.recordAIQuestion(lessonId)

    setIsLoadingAI(true)
    setShowAIExplanation(true)

    try {
      // Format câu hỏi cho AI (dùng shuffledData.options)
      const formattedQuestion = `${question}\n\nCác lựa chọn:\n${shuffledData.options.map((opt, idx) => `${String.fromCharCode(65 + idx)}. ${opt}`).join('\n')}`

      // Askbot AI — Socrates: AI KHÔNG trả lời thay, chỉ đặt câu hỏi nhỏ gợi mở
      // để người học tự suy nghĩ và tự đưa ra quyết định.
      const response = await aiService.explainContent({
        content: formattedQuestion,
        contentType: 'socratic',
        context: 'Câu hỏi tương tác trong bài học Vật lí — học sinh đang tự làm',
        lessonId: lessonId,
        usage: {
          userId: user.id,
          feature: 'interactive_question',
          metadata: {
            lessonId
          }
        }
      })
      
      if (response.success && response.explanation) {
        setAIExplanation(response.explanation)
      } else {
        setAIExplanation(response.error || '⚠️ Không thể kết nối với AI. Vui lòng thử lại sau.')
      }
    } catch (error) {
      console.error('Error asking AI:', error)
      setAIExplanation(
        error instanceof Error ? `⚠️ ${error.message}` : '⚠️ Có lỗi xảy ra khi kết nối với AI.'
      )
    } finally {
      setIsLoadingAI(false)
    }
  }

  return (
    <div ref={containerRef} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-1 sm:p-2 my-1 sm:my-2">
      <div className="flex items-start justify-between mb-1 sm:mb-2">
        <div className="flex-1">
          <div className="flex items-center space-x-1 mb-1">
            <span className="text-sm sm:text-base">🤔</span>
            <span className={`px-1 py-0.5 rounded-full text-[0.6rem] sm:text-xs font-medium ${getDifficultyColor()}`}>
              {getDifficultyText()}
            </span>
          </div>
          <div className="text-gray-700 leading-tight text-sm sm:text-base">
            <KaTeXRenderer content={question} />
          </div>
          
          {/* ✅ Question Image - Centered and Responsive */}
          {image && (
            <div className="mt-3 mb-3 flex justify-center">
              <div className="relative">
                <img 
                  src={image} 
                  alt={imageAlt || 'Hình minh họa câu hỏi'}
                  className="rounded-lg shadow-md border border-gray-200 mx-auto max-h-[200px] sm:max-h-[280px] md:max-h-[320px]"
                  style={{ 
                    maxWidth: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    width: 'auto'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    console.warn('Question image failed to load:', image)
                    target.style.display = 'none'
                  }}
                />
                {/* 📊 Graph indicator badge */}
                <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                  📊
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hint Button và Ask AI Button */}
      <div className="mb-1 flex items-center gap-2">
        {hint && !showResult && (
          <button
            onClick={() => {
              // 📊 Track hint usage (only when showing, not hiding)
              if (!showHint) {
                LessonStatsService.recordHintUsed(lessonId)
              }
              setShowHint(!showHint)
            }}
            className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            <Lightbulb className="w-3 h-3" />
            <span>{showHint ? 'Ẩn gợi ý' : 'Hiển thị gợi ý'}</span>
          </button>
        )}
        
        {/* Ask AI Button */}
        <button
          onClick={handleAskAI}
          disabled={isLoadingAI}
          className="flex items-center space-x-1 text-xs text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Sparkles className="w-3 h-3" />
          <span>{isLoadingAI ? 'Askbot đang nghĩ...' : '🤖 Askbot AI'}</span>
        </button>
      </div>
      
      {hint && showHint && (
        <div className="mb-1 p-1.5 bg-yellow-50 border border-yellow-200 rounded">
          <div className="text-xs text-yellow-800 leading-tight">
            💡 <KaTeXRenderer content={hint} />
          </div>
        </div>
      )}

      {/* Options */}
      <div className="space-y-1 mb-1">
        {shuffledData.options.map((option, index) => {
          let buttonClass = "w-full text-left p-1.5 sm:p-2 rounded border transition-all duration-200 "
          
          // ✅ FIX: Cho phép chọn lại khi sai, nhưng không cho chọn khi đã completed
          const isCorrectAnswer = selectedAnswer === shuffledData.correctAnswer
          const canRetry = showResult && !isCorrectAnswer && !isCompleted // Cho phép chọn lại nếu đã hiện kết quả và chưa đúng và chưa completed
          
          if ((!showResult && !isCompleted) || canRetry) {
            // Chưa chọn hoặc có thể chọn lại
            buttonClass += "border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer"
          } else {
            // Đã chọn đúng hoặc đã completed - không cho chọn lại
            buttonClass += "pointer-events-none "
            if (index === shuffledData.correctAnswer) {
              buttonClass += "border-green-500 bg-green-50 text-green-800"
            } else if (index === selectedAnswer && index !== shuffledData.correctAnswer) {
              buttonClass += "border-red-500 bg-red-50 text-red-800"
            } else {
              buttonClass += "border-gray-200 bg-gray-50 text-gray-600"
            }
          }

          return (
            <button
              key={index}
              onClick={(e) => {
                e.preventDefault() // ✅ Chặn default behavior
                e.stopPropagation() // ✅ Chặn event bubbling
                handleAnswerSelect(index)
              }}
              className={buttonClass}
              disabled={isCompleted || (showResult && selectedAnswer === shuffledData.correctAnswer)} // ✅ Disable khi đã completed hoặc đã chọn đúng
              type="button" // ✅ Explicit button type
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 text-sm sm:text-base leading-tight">
                  <KaTeXRenderer content={option} />
                </div>
                {showResult && (
                  <div className="ml-1">
                    {index === shuffledData.correctAnswer ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : index === selectedAnswer ? (
                      <XCircle className="w-4 h-4 text-red-600" />
                    ) : null}
                  </div>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {/* Result */}
      {showResult && (
        <div 
          className={`p-1.5 sm:p-2 rounded mb-1 animate-slide-up ${
            selectedAnswer === shuffledData.correctAnswer 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}
          style={{
            animation: 'slideUpFade 0.4s ease-out'
          }}
        >
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-1">
              {selectedAnswer === shuffledData.correctAnswer ? (
                <>
                  <CheckCircle
                    className="w-4 h-4 text-green-600"
                    style={{ animation: 'gentleBounce 0.6s ease-in-out' }}
                  />
                  <span className="font-medium text-green-800 text-sm">🎉 Chính xác! Tuyệt vời!</span>
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="font-medium text-red-800 text-sm">{WRONG_ENCOURAGE}</span>
                </>
              )}
            </div>
            
            {/* Try Again Button - Only show when wrong answer and not completed */}
            {selectedAnswer !== shuffledData.correctAnswer && !isCompleted && (
              <button
                onClick={reset}
                className="flex items-center space-x-1 px-2 py-1 bg-blue-600 text-white font-medium rounded text-xs hover:bg-blue-700 transition-colors duration-200"
              >
                <ArrowRight className="w-3 h-3" />
                <span>Thử lại</span>
              </button>
            )}
          </div>
          
          {showExplanation && (
            <div className="mt-1 p-1.5 bg-white rounded border border-gray-200">
              <div className="text-gray-700 text-xs leading-tight">
                {explanation && explanation.trim()
                  ? <KaTeXRenderer content={explanation} />
                  : (selectedAnswer === shuffledData.correctAnswer
                      ? '✔ Chính xác! (Bài giảng chưa soạn lời giải chi tiết cho câu này.)'
                      : '✘ Chưa đúng — đáp án đúng đã được tô xanh.')}
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI Explanation */}
      {showAIExplanation && !isLoadingAI && (
        <div className="p-1.5 sm:p-2 rounded bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 mt-1">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-1">
              <Sparkles className="w-4 h-4 text-purple-600" />
              <span className="font-medium text-purple-800 text-sm">Askbot AI — cùng suy nghĩ nhé</span>
            </div>
            <button
              onClick={() => setShowAIExplanation(false)}
              className="text-xs text-purple-600 hover:text-purple-700 font-medium"
            >
              Ẩn
            </button>
          </div>
          
          <div className="mt-1 p-1.5 bg-white rounded border border-purple-100">
            <div className="text-gray-700 text-xs leading-tight">
              <KaTeXRenderer content={aiExplanation} />
            </div>
          </div>
        </div>
      )}

      {/* ✨ AI Thinking Overlay - Hiệu ứng đặc biệt */}
      {isLoadingAI && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div 
            className="bg-gradient-to-br from-purple-600 via-pink-500 to-indigo-600 rounded-3xl p-8 shadow-2xl"
            style={{
              animation: 'slideUpFade 0.3s ease-out, gentleBounce 2s ease-in-out infinite'
            }}
          >
            <div className="flex flex-col items-center space-y-4">
              {/* Icon AI với hiệu ứng pulse và glow */}
              <div className="relative">
                <div className="absolute inset-0 bg-white/30 rounded-full blur-xl animate-pulse"></div>
                <Sparkles 
                  className="w-16 h-16 text-white relative z-10" 
                  strokeWidth={2}
                  style={{
                    filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.8))',
                    animation: 'spin 3s linear infinite'
                  }}
                />
              </div>
              
              {/* Text */}
              <div className="text-center">
                <p className="text-white font-bold text-xl mb-1">Askbot đang nghĩ câu gợi mở...</p>
                <p className="text-purple-100 text-sm">Askbot sẽ đặt câu hỏi để bạn tự tìm ra đáp án</p>
              </div>
              
              {/* Loading dots */}
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default InteractiveQuestion